import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SkipToContent } from "@/components/SkipToContent";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Skeleton } from "@/components/ui/skeleton";

// Lazy-loaded pages
const Index = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const Resources = lazy(() => import("./pages/Resources"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminResources = lazy(() => import("./pages/AdminResources"));
const AdminContacts = lazy(() => import("./pages/AdminContacts"));
const AdminCareers = lazy(() => import("./pages/AdminCareers"));
const AdminNewsletter = lazy(() => import("./pages/AdminNewsletter"));
const AdminEvents = lazy(() => import("./pages/AdminEvents"));
const AdminTeam = lazy(() => import("./pages/AdminTeam"));
const AdminBoard = lazy(() => import("./pages/AdminBoard"));
const AdminMembers = lazy(() => import("./pages/AdminMembers"));
const AdminAnnouncements = lazy(() => import("./pages/AdminAnnouncements"));
const AdminDocuments = lazy(() => import("./pages/AdminDocuments"));
const AdminTestimonials = lazy(() => import("./pages/AdminTestimonials"));
const AdminNews = lazy(() => import("./pages/AdminNews"));
const AdminAuditLog = lazy(() => import("./pages/AdminAuditLog"));
const AdminGroups = lazy(() => import("./pages/AdminGroups"));
const AdminCommunications = lazy(() => import("./pages/AdminCommunications"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const MemberDashboard = lazy(() => import("./pages/MemberDashboard"));
const About = lazy(() => import("./pages/About"));
const AboutCouncil = lazy(() => import("./pages/AboutCouncil"));
const AboutFoundation = lazy(() => import("./pages/AboutFoundation"));
const AboutPAC = lazy(() => import("./pages/AboutPAC"));
const CouncilTeam = lazy(() => import("./pages/CouncilTeam"));
const BoardOfDirectors = lazy(() => import("./pages/BoardOfDirectors"));
const Membership = lazy(() => import("./pages/Membership"));
const Committees = lazy(() => import("./pages/Committees"));
const Accomplishments = lazy(() => import("./pages/Accomplishments"));
const CurrentMembers = lazy(() => import("./pages/CurrentMembers"));
const Foundation = lazy(() => import("./pages/Foundation"));
const NMCProgram = lazy(() => import("./pages/NMCProgram"));
const Events = lazy(() => import("./pages/Events"));
const Contact = lazy(() => import("./pages/Contact"));
const Careers = lazy(() => import("./pages/Careers"));
const News = lazy(() => import("./pages/News"));
const NewsArticle = lazy(() => import("./pages/NewsArticle"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AdminLayout = lazy(() => import("./components/AdminLayout"));

const queryClient = new QueryClient();

function PageLoader() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-16 space-y-4">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-4 w-96" />
      <Skeleton className="h-64 w-full mt-6" />
    </div>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem storageKey="achs-theme">
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <SkipToContent />
          <Navbar />
          <main id="main-content">
          <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/login" element={<Login />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="resources" element={<AdminResources />} />
              <Route path="contacts" element={<AdminContacts />} />
              <Route path="careers" element={<AdminCareers />} />
              <Route path="newsletter" element={<AdminNewsletter />} />
              <Route path="events" element={<AdminEvents />} />
              <Route path="team" element={<AdminTeam />} />
              <Route path="board" element={<AdminBoard />} />
              <Route path="members" element={<AdminMembers />} />
              <Route path="announcements" element={<AdminAnnouncements />} />
              <Route path="documents" element={<AdminDocuments />} />
              <Route path="testimonials" element={<AdminTestimonials />} />
              <Route path="news" element={<AdminNews />} />
              <Route path="audit-log" element={<AdminAuditLog />} />
              <Route path="groups" element={<AdminGroups />} />
              <Route path="communications" element={<AdminCommunications />} />
              <Route path="users" element={<AdminUsers />} />
            </Route>
            <Route path="/dashboard" element={<MemberDashboard />} />
            <Route path="/about" element={<About />} />
            <Route path="/about/council" element={<AboutCouncil />} />
            <Route path="/about/foundation" element={<AboutFoundation />} />
            <Route path="/about/pac" element={<AboutPAC />} />
            <Route path="/about/team" element={<CouncilTeam />} />
            <Route path="/about/board" element={<BoardOfDirectors />} />
            <Route path="/membership" element={<Membership />} />
            <Route path="/membership/committees" element={<Committees />} />
            <Route path="/membership/accomplishments" element={<Accomplishments />} />
            <Route path="/membership/members" element={<CurrentMembers />} />
            <Route path="/foundation" element={<Foundation />} />
            <Route path="/foundation/nmc" element={<NMCProgram />} />
            <Route path="/events" element={<Events />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/news" element={<News />} />
            <Route path="/news/:id" element={<NewsArticle />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </ErrorBoundary>
          </main>
          <Footer />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
